<template>
  <div class="did">
    <shell @pageend="pageItems">
      <div class="container m-auto">
        <div class="w-full lg:w-1/2 m-auto">
          <note-select :path="path" :tags="tags" page="did" :params="routeParams" type="Events" />

          <div class="px-2">

            <div class="flex items-center pb-4">
              <div class="mr-2 text-xs underline unselectable cursor-pointer"
                :class="{
                  'text-full-blue': !(this.mode === 'YR'),
                  'text-black font-bold no-underline': this.mode === 'YR'}"
                @click="changeRoute({mode:'YR'})">Year</div>
              <div class="mr-2 text-xs underline text-full-blue unselectable cursor-pointer"
                :class="{
                  'text-full-blue': !(this.mode === 'MO'),
                  'text-black font-bold no-underline': this.mode === 'MO'}"
                @click="changeRoute({mode:'MO'})">Month</div>
              <div class="mr-2 text-xs underline text-full-blue unselectable cursor-pointer"
                :class="{
                  'text-full-blue': !(this.mode === 'WK'),
                  'font-bold no-underline text-black': this.mode === 'WK'}"
                @click="changeRoute({mode:'WK'})">Week</div>
              <div class="mr-2 text-xs underline text-full-blue unselectable cursor-pointer"
                :class="{
                  'text-full-blue': !(this.mode === 'DAY'),
                  'text-black font-bold no-underline': this.mode === 'DAY'}"
                @click="changeRoute({mode:'DAY'})">Day</div>
            </div>

            <div class="flex justify-between pb-4 text-xs items-center">
              <div class="text-full-blue underline unselectable cursor-pointer" @click="prevPeriod">Prev {{dateLabel}}</div>
              <div class="text-lg"><strong>{{displayDate}}</strong></div>
              <div class="text-full-blue underline unselectable cursor-pointer" @click="nextPeriod">Next {{dateLabel}}</div>
            </div>

            <div class="border-t-4 border-black pt-4 mb-4">
              <div class="pb-4" v-if="items.colors.length">
                <div class="pb-2 text-sm font-bold">Key</div>
                <div class="flex flex-wrap">
                  <div v-for="color in items.colors" class="flex items-center pr-4">
                    <div
                      class="mark" v-html="parsedSymbol(color.hex)" />
                    <div class="font-mono pl-2 text-gray text-sm" v-html="parsedSymbol(color.symbol)" />
                  </div>
                </div>
              </div>
              <div v-if="items.others.length">
                <div class="pb-2 text-sm font-bold">Symbols</div>
                <div class="flex items-center flex-wrap">
                  <div v-for="other in items.others" class="pr-4" v-html="parsedSymbol(other)" />
                </div>
              </div>

              <div v-if="!items.colors.length && !items.others.length">
                <t-text kind="INFO" lvl="2" class="mt-2">No events for {{displayDate}}.</t-text>
              </div>
            </div>
            <div class="pb-4">
              <div v-for="item in items.dates"
                class="date-row flex py-2 items-center border-b border-gray-light">
                <div class="text-xs pr-4 font-mono underline text-full-blue unselectable cursor-pointer"
                  v-if="item.navToMode"
                  @click="changeRoute({mode: item.navToMode, date: item.isoDate})"
                >
                  {{item.date}}
                </div>
                <div v-else
                  class="text-xs pr-4 font-mono text-gray">{{item.date}}</div>

                <div class="flex items-center">
                  <div v-for="bang in item.bangs" class="p-1 flex items-center mr-1">
                    <div v-html="parsedSymbol(bang.symbol)" />

                    <div v-if="bang.count > 1" class="ml-1 text-xs font-mono font-bold">x{{bang.count}}</div>
                  </div>
                </div>
              </div>
            </div>
            <record-list :records="records" :path="path" />
          </div>
        </div>
      </div>
    </shell>
  </div>
</template>

<script>
 import { DateTime } from 'luxon';
 import { mapState, mapActions } from 'vuex';
 import eventBus from '@/eventBus';
 import Shell from '@/components/Shell';
 import NoteSelect from '@/components/NoteSelect';
 import RecordList from '@/components/RecordList';
 import api from '@/api';

 const isOneChar = (str) => {
   // externalize
   var surrogatePairs = str.match(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g) || [];
   return (str.length - surrogatePairs.length) === 1;
 }
 const config = {
   ALL: {
     key: 'yyyyLLdd',
     interval: 'LLL dd, yyyy'
   },
   YR: {
     key: 'yyyyLL',
     interval: 'LLL yyyy',
     period: 'years',
     display: 'yyyy',
     label: 'Year',
     normalize: d => DateTime.fromISO(d).startOf('year').toISO(),
     navToMode: 'MO',
   },
   MO: {
     key: 'yyyyLLdd',
     interval: 'LLL dd',
     period: 'months',
     display: 'LLL yyyy',
     label: 'Month',
     normalize: d => DateTime.fromISO(d).startOf('month').toISO(),
     navToMode: 'DAY',
   },
   WK: {
     key: 'yyyyLLdd',
     interval: 'EEE dd',
     period: 'weeks',
     display: "'Week of' LLL dd, yyyy",
     label: 'Week',
     normalize: d => DateTime.fromISO(d).startOf('week').toISO(),
     navToMode: 'DAY',
   },
   DAY: {
     key: 'yyyyLLddHH',
     interval: 'LLL dd hh:mm a',
     period: 'days',
     display: "LLL dd, yyyy",
     label: 'Day',
     normalize: d => DateTime.fromISO(d).startOf('day').toISO()
   }
 }

 export default {
   components: { Shell, NoteSelect, RecordList },
   props: {
     path: {
       type: String,
       default: ''
     },

     tags: {
       type: Array,
       default: []
     },

     date: {
       required: true
     },

     mode: {
       type: String,
       default: 'WK'
     }
   },

   data () {
     return {
       dates: [],
       isEnd: false,
       recordOffset: 0,
       isInProgress: false
     }
   },

   computed: {
     ...mapState({
       records: state => state.records
     }),
     items () {
       const dates = {};

       /* ['#ff5c57',
        *  '#5af78e',
        *  '#f3f99d',
        *  '#57c7ff',
        *  '#ff6ac1',
        *  '#9aedfe'] */

       const palette = [
         '🟢', '🔵', '🔴', '🟠', '🟣', '🟤', '🟡', // circles green, blue, red, orange, purple, brown, yellow
         '🟩', '🟦', '🟥', '🟧', '🟪', '🟫',  '🟨' // squares green, blue, red, orange, purple, brown, yellow
       ];

       const colors = {};
       const others = {};
       this.dates.forEach(d => {

			   const date = DateTime.fromISO(d.start_at).toFormat(config[this.mode].key);

         if (date in dates === false) {
           dates[date] = {
             symbols: [],
             startAt: d.start_at
           }
         }

         if (d.symbol) {
           const isOne = isOneChar(d.symbol);

           let color;

           if (isOne === false) {
             if (colors[d.symbol]) {
               color = colors[d.symbol];
             } else {
               colors[d.symbol] = palette[Object.keys(colors).length % palette.length];
               color = colors[d.symbol];
             }
           } else {
             others[d.symbol] = true
           }

           dates[date].symbols.push({
             symbol: d.symbol,
             count: d.count,
             isOneChar: isOne,
             color
           })
         }
       })

       return {
         dates: Object.keys(dates).sort().map(dateKey => {
           const startAt = dates[dateKey].startAt

           const dateObj = {
             date: DateTime.fromISO(startAt).toFormat(config[this.mode].interval),
             isoDate: DateTime.fromISO(startAt).toISODate(),
             navToMode: config[this.mode].navToMode
           }

           dateObj.bangs = dates[dateKey]
             .symbols
             .reduce((acc, next) => {
               const mark = next.isOneChar ?  next.symbol : next.color;

               let symbol = '';

               if (next.count > 1) {
                 symbol = mark;
               } else {
                 for (let i=0; i < next.count; i++) {
                   symbol += mark;
                 }
               }

               return acc.concat([Object.assign(next, {symbol})]);
             }, [])
             .sort((a, b) => {
               return a.symbol > b.symbol ? -1 : a.symbol < b.symbol ? 1 : 0
             })

           return dateObj;
         }),
         colors: Object.keys(colors).map(c => ({
           symbol: c,
           hex: colors[c]
         })),
         others: Object.keys(others)
       }
     },

     displayDate () {
       const date = config[this.mode].normalize(this.date);

       return DateTime.fromISO(date).toFormat(config[this.mode].display);
     },

     dateLabel () {
       return config[this.mode].label
     },

     routeParams () {
       return {
         date: 'd:' + this.date,
         mode: 'm:' + this.mode
       }
     },

     start () {
       return DateTime.fromISO(config[this.mode].normalize(this.date))
                      .toISO();
     },

     end () {
       return DateTime.fromISO(this.start)
                      .plus({[config[this.mode].period]: 1})
                      .toISO();
     }
   },

   async created () {
     await this.bootstrap();
     eventBus.$on('record', this.bootstrap);
   },

   destroyed () {
     eventBus.$off('record', this.bootstrap);
   },

   methods: {
     ...mapActions(['getRecords', 'getPaths', 'getTags']),
     async pageItems (done = () => {}) {
       if (this.isInProgress || this.isEnd) {
         return done();
       }

       this.isInProgress = true;
       this.recordOffset = this.recordOffset + 1

       const res = await this.getRecords({
         type: 'bang',
         path: this.path,
         tags: this.tags,
         todo: this.todo,
         page: this.recordOffset,
         start: this.start,
         end: this.end
       });

       if ((res && res.length < 10) || (res && res.err)) {
         this.isEnd = true;
       }

       this.$nextTick(() => {
         done();
         this.isInProgress = false;
       });
     },
     async bootstrap () {
       this.getPaths();
       this.getTags();
       this.recordOffset = 0;
       this.isInProgress = true;
       this.isEnd = false;

       const date = config[this.mode].normalize(this.date);

       const {dates} = await api.getBangs(this.path, this.tags, date, this.mode);

       const res = await this.getRecords({
         type: 'bang',
         path: this.path,
         tags: this.tags,
         todo: this.todo,
         start: this.start,
         end: this.end
       });

       if ((res && res.length < 10) || (res && res.err)) {
         this.isEnd = true;
       }

       this.isInProgress = false;

       this.dates = dates;
     },
     
     parsedSymbol (sym) {
       return twemoji.parse(sym)
     },
     nextPeriod () {
       this.changeRoute({date: DateTime.fromISO(this.date).plus({[config[this.mode].period]: 1}).toISODate()})
     },

     prevPeriod () {
       this.changeRoute({date: DateTime.fromISO(this.date)
                                       .minus({[config[this.mode].period]: 1}).toISODate()})
     },

     changeRoute ({mode, date}) {
       this.$nav(
         'events',
         {
           extra: ':m:' + (mode || this.mode) + '/:d:' + (date || this.date)
         }
       );
     },
   },
   watch: {
     $route (to) {
       if (to.name !== 'logout') {
         this.bootstrap();
       }
     }
   }
 };
</script>


<style>
  .date-row {
  height: 40px;
  }

  

</style>
